import { useContext, useEffect } from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type { Keys } from '@sorare/wallet-shared';

import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import {
  Level,
  useSnackNotificationContext,
} from '#src/contexts/snackNotification/index.ts';
import { useFetchEncryptedPrivateKeyMutation } from '#src/hooks/auth/useFetchEncryptedPrivateKeyMutation.ts';
import type { Error as MutationError } from '#src/hooks/graphql/useMutation.ts';
import { errorCodes } from '#src/hooks/graphql/useMutation.ts';

const MISSING_OTP_CODE = 1000;
const INVALID_OTP_CODE = 1001;
const UNKNOWN_FINGERPRINT_CODE = 1002;
const UNKNOWN_FINGERPRINT_MESSAGE = 'unknown_fingerprint';

const isOtpError = ({ code }: MutationError) =>
  code === MISSING_OTP_CODE || code === INVALID_OTP_CODE;

// The backend does not send a code for this one yet, so match the message too.
const isUnknownFingerprintError = ({ code, message }: MutationError) =>
  code === UNKNOWN_FINGERPRINT_CODE || message === UNKNOWN_FINGERPRINT_MESSAGE;

const isHandledByUseMutation = ({ code }: MutationError) =>
  code !== undefined && Object.values<number>(errorCodes).includes(code);

export const useKeys = () => {
  const { registerHandler } = useContext(MessagingContext)!;
  const { sorareEncryptionKey } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const [fetchEncryptedPrivateKey] = useFetchEncryptedPrivateKeyMutation();
  const { showNotification } = useSnackNotificationContext();

  useEffect(
    () =>
      registerHandler<Keys>('keys', async () => {
        if (!currentUser) return { result: { sorareEncryptionKey } };

        const { sorarePrivateKey, otpRequiredForLogin } = currentUser;
        if (!otpRequiredForLogin)
          return {
            result: {
              userPrivateKey: sorarePrivateKey || undefined,
              sorareEncryptionKey,
            },
          };

        // useMutation flattens network and top-level GraphQL errors into
        // `errors` and never throws, so this is the only failure signal.
        const { data, errors } = await fetchEncryptedPrivateKey({
          variables: { input: {} },
        });

        const noKey = {
          result: { userPrivateKey: undefined, sorareEncryptionKey },
        };

        if (errors.some(isOtpError))
          return { ...noKey, error: 'invalid-otp' as const };

        if (errors.some(isUnknownFingerprintError)) {
          showNotification('unconfirmedDevice', undefined, {
            level: Level.ERROR,
          });
          return { ...noKey, error: 'unconfirmed-device' as const };
        }

        const payload = data?.fetchEncryptedPrivateKey;

        if (payload?.sorarePrivateKey)
          return {
            result: {
              userPrivateKey: payload.sorarePrivateKey,
              sorareEncryptionKey,
            },
          };

        const fetchFailed = !payload || errors.length > 0;
        if (fetchFailed) {
          const everyErrorHandledElsewhere =
            errors.length > 0 && errors.every(isHandledByUseMutation);

          if (!everyErrorHandledElsewhere)
            showNotification('walletKeysUnavailable', undefined, {
              level: Level.ERROR,
            });

          return { ...noKey, error: 'key-fetch-failed' as const };
        }

        return noKey;
      }),
    [
      currentUser,
      registerHandler,
      fetchEncryptedPrivateKey,
      sorareEncryptionKey,
      showNotification,
    ]
  );
};
